import React, { useEffect, useState } from "react";
import { Form, Input, Select, DatePicker, Button, Card, Row, Col, Drawer, message, Divider } from "antd";
import dayjs from "dayjs";
import API from "../../../api";
import ConfirmationModal from "../../../components/ConfirmationModel";
import { getUserRole } from "../../../utils/auth";

const { Option } = Select;

const SupplierPaymentDrawer = ({ open, setDrawerVisible, selectedPayment, handleSubmit, selectedSupplierData }) => {
    const [form] = Form.useForm();
    const [suppliers, setSuppliers] = useState([]);
    const [stores, setStores] = useState([]);
    const [supplierData, setSupplierData] = useState({
        openingBalance: 0,
        currentBalance: 0,
        closingBalance: 0,
    });
    const [loading, setLoading] = useState(false);
    const [modal, setModal] = useState(false);
    const [filters, setFilters] = useState({ storeName: null });
    const [userRole, setUserRole] = useState(null); // Store user role

    const fetchSuppliers = async () => {
        try {
            const { data } = await API.get("/suppliers", {
                params: { storeName: filters.storeName || undefined },
            });
            setSuppliers(data.suppliers || []);
        } catch (error) {
            message.error("Failed to fetch suppliers.");
            console.error("Error fetching suppliers:", error);
        }
    };

    const fetchStores = async () => {
        try {
            const { data } = await API.get("/stores");
            setStores(data || []);
            console.log("stores", data); // Log stores after fetch
            return data; // Return data for use in .then
        } catch (error) {
            message.error("Failed to fetch stores.");
            console.error("Error fetching stores:", error);
            return [];
        }
    };

    const fetchSupplierPayments = async (supplierId) => {
        try {
            const { data } = await API.get(`/payments/supplier/${supplierId}`);
            setSupplierData({
                openingBalance: data.totals.totalAmount,
                currentBalance: data.totals.paidAmount,
                closingBalance: data.totals.balanceAmount,
            });
        } catch (error) {
            console.error("Error fetching supplier payments:", error);
            setSupplierData({ openingBalance: 0, currentBalance: 0, closingBalance: 0 });
        }
    };

    const handleConfirm = async (values) => {
        setLoading(true);
        try {
            const payload = {
                ...values,
                supplierId: form.getFieldValue("supplierId"),
                type: "pay",
            };
            if (selectedPayment) {
                await API.put(`/payments/${selectedPayment._id}`, payload);
                message.success("Payment updated successfully.");
            } else {
                await API.post("/payments", payload);
                message.success("Payment added successfully.");
            }
            setDrawerVisible(false);
            fetchSupplierPayments(form.getFieldValue("supplierId"));
            form.resetFields();
            handleSubmit();
        } catch (error) {
            message.error(error?.response?.data?.message || "Failed to process payment.");
            console.error("Error processing payment:", error);
        } finally {
            setLoading(false);
            setModal(false);
        }
    };

    const validatePaymentLimit = (_, value) => {
        const paidAmount = parseFloat(value) || 0;
        const maxAllowed = supplierData.closingBalance + (selectedPayment?.paid_amount || 0);
        if (paidAmount > maxAllowed) {
            return Promise.reject(new Error(`Amount cannot exceed Remaining Balance (₹${maxAllowed.toLocaleString()})`));
        }
        return Promise.resolve();
    };

    const handleSupplierChange = (supplierId) => {
        form.setFieldsValue({ supplierId });
        fetchSupplierPayments(supplierId);
    };

    // Fetch user role, stores, and set default storeName on mount
    useEffect(() => {
        const role = getUserRole();
        setUserRole(role);
        console.log("role", role);

        fetchStores().then((storeData) => {
            if (role.role !== "admin" && role.storeId) {
                const userStore = storeData.find((store) => store._id === role.storeId);
                if (userStore) {
                    setFilters((prev) => ({ ...prev, storeName: userStore.name }));
                } else {
                    console.warn("No store found for storeId:", role.storeId);
                }
            }
        });

        fetchSuppliers();
        if (selectedSupplierData) {
            fetchSupplierPayments(selectedSupplierData);
        }
    }, [selectedSupplierData]); // Run when selectedSupplierData changes

    // Refetch suppliers when storeName changes
    useEffect(() => {
        fetchSuppliers();
    }, [filters.storeName]);

    // Set form fields when selectedPayment or selectedSupplierData changes
    useEffect(() => {
        if (selectedPayment) {
            form.setFieldsValue({
                supplierId: selectedPayment.supplierId,
                paymentDate: dayjs(selectedPayment.paymentDate),
                paymentMethod: selectedPayment.paymentMethod,
                paid_amount: selectedPayment.paid_amount,
                transactionId: selectedPayment.transactionId,
                remarks: selectedPayment.remarks,
            });
        } else if (selectedSupplierData) {
            form.setFieldsValue({
                supplierId: selectedSupplierData,
                paymentDate: dayjs(),
                paid_amount: "",
            });
        } else {
            form.resetFields();
        }
    }, [selectedPayment, selectedSupplierData, form]);

    return (
        <Drawer
            title={selectedPayment ? "Edit Payment" : "Add Payment"}
            width={1000}
            onClose={() => setDrawerVisible(false)}
            open={open}
            bodyStyle={{ paddingBottom: 80 }}
        >
            <Form form={form} layout="vertical" onFinish={(values) => setModal(true)}>
                <Row gutter={16}>
                    <Col xs={24} sm={12} md={12}>
                        <Form.Item
                            name="supplierId"
                            label="Select Supplier"
                            rules={[{ required: true, message: "Please select a supplier!" }]}
                        >
                            <Select
                                placeholder="Select Supplier"
                                showSearch
                                filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                                onChange={handleSupplierChange}
                                disabled={!!selectedPayment}
                            >
                                {suppliers.map((supplier) => (
                                    <Option key={supplier._id} value={supplier._id}>
                                        {supplier.name || "Unnamed Supplier"}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Form.Item
                            name="paymentDate"
                            label="Payment Date"
                            rules={[{ required: true, message: "Please select a payment date!" }]}
                        >
                            <DatePicker style={{ width: "100%" }} disabled={!!selectedPayment} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Form.Item
                            name="paymentMethod"
                            label="Payment Method"
                            rules={[{ required: true, message: "Please select a payment method!" }]}
                        >
                            <Select placeholder="Select Payment Method">
                                {["cash", "upi", "credit_card", "debit_card", "bank_transfer", "others"].map((method) => (
                                    <Option key={method} value={method}>{method.replace("_", " ").toUpperCase()}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Form.Item
                            name="paid_amount"
                            label="Amount"
                            rules={[
                                { required: true, message: "Please enter the amount!" },
                                { validator: validatePaymentLimit },
                            ]}
                        >
                            <Input
                                type="number"
                                placeholder="Enter amount"
                                min={0}
                                disabled={supplierData.closingBalance <= 0 && !selectedPayment}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Form.Item name="transactionId" label="Transaction ID/Reference">
                            <Input placeholder="Enter transaction ID/reference" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={10}>
                        <Form.Item label="Select Store">
                            <Select
                                showSearch
                                placeholder="Select Store"
                                value={filters.storeName}
                                onChange={(value) => setFilters({ ...filters, storeName: value })}
                                style={{ width: "100%" }}
                                allowClear
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().includes(input.toLowerCase())
                                }
                                disabled={userRole?.role !== "admin"} // Disable for non-admin users
                            >
                                {stores.map((store) => (
                                    <Option key={store._id} value={store.name}>
                                        {store.name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={24}>
                        <Form.Item name="remarks" label="Remarks">
                            <Input.TextArea rows={2} placeholder="Enter remarks (optional)" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Form.Item>
                            <Button type="primary" htmlType="submit" style={{ width: "100%" }} loading={loading}>
                                Submit
                            </Button>
                        </Form.Item>
                    </Col>
                </Row>
                <Divider />
                <Card title="Supplier Summary" bordered={false} style={{ backgroundColor: "#f6f6f6" }}>
                    <Row gutter={[16, 16]}>
                        <Col span={8}>
                            <h5>Total Amount</h5>
                            <p>₹{supplierData.openingBalance.toLocaleString()}</p>
                        </Col>
                        <Col span={8}>
                            <h5>Paid Balance</h5>
                            <p>₹{supplierData.currentBalance.toLocaleString()}</p>
                        </Col>
                        <Col span={8}>
                            <h5>Remaining Balance</h5>
                            <p>₹{supplierData.closingBalance.toLocaleString()}</p>
                        </Col>
                    </Row>
                </Card>
            </Form>

            <ConfirmationModal
                visible={modal}
                title="Confirm Payment"
                message="Are you sure you want to submit this payment?"
                onConfirm={() => handleConfirm(form.getFieldsValue())}
                onCancel={() => setModal(false)}
                confirmText="Confirm"
                cancelText="Cancel"
                loading={loading}
            />
        </Drawer>
    );
};

export default SupplierPaymentDrawer;